import { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { useDatasets } from "../context/DatasetContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  UploadCloud,
  FileSpreadsheet,
  CheckCircle2,
  AlertCircle,
  Trash2,
  Calendar,
  Hash,
  Siren,
  ExternalLink,
  Pencil,
  Download,
  Check,
  X,
  ArrowUpDown,
  Clock,
  Ban,
  Globe,
  Filter,
  Loader2,
  Database,
  ScanLine,
  Sparkles,
} from "lucide-react";
import { Input } from "../components/ui/input";
import { authHeaders } from "../utils/api";
import { useAuth } from "../context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// UploadProgressPanel
// Rich, phase-aware progress panel that surfaces every backend milestone so
// large-file uploads never feel "stalled". Shows an active phase pill, an
// activity heartbeat, per-phase counters (records inserted, batches done),
// elapsed time / ETA, and a mini timeline of completed phase durations.
// ---------------------------------------------------------------------------
const PHASE_ORDER = ["uploading", "parsing", "inserting", "anomalies", "complete"];
const PHASE_META = {
  uploading:  { label: "Uploading",         icon: UploadCloud,    tip: "Streaming file to server" },
  parsing:    { label: "Parsing CSV",       icon: ScanLine,       tip: "Reading rows and columns" },
  inserting:  { label: "Importing to DB",   icon: Database,       tip: "Writing records to MongoDB in parallel batches" },
  anomalies:  { label: "Detecting Anomalies", icon: Siren,        tip: "Scanning for suspicious patterns" },
  complete:   { label: "Finalizing",        icon: Sparkles,       tip: "Wrapping up" },
};

function fmtSecs(n) {
  if (!n) return "0s";
  const m = Math.floor(n / 60);
  const s = Math.round(n % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function UploadProgressPanel({
  uploadProgress,
  processingPhase,
  processingRecordCount,
  recordsInserted,
  batchesTotal,
  batchesCompleted,
  phaseTimings,
  elapsedSeconds,
  onCancel,
}) {
  // Derive current phase. Prefer explicit backend value; fall back to
  // progress-based inference for the initial ~1s before the backend writes
  // its first phase update.
  const currentPhase = processingPhase && PHASE_META[processingPhase]
    ? processingPhase
    : uploadProgress < 50
      ? "uploading"
      : uploadProgress < 55
        ? "parsing"
        : uploadProgress < 90
          ? "inserting"
          : uploadProgress < 98
            ? "anomalies"
            : "complete";

  const CurrentIcon = PHASE_META[currentPhase].icon;

  // Compute a smart ETA using the backend progress once we have real data.
  let elapsedLabel = fmtSecs(elapsedSeconds);
  let etaLabel = "";
  if (uploadProgress > 52 && uploadProgress < 99) {
    const backendPct = (uploadProgress - 50) * 2;
    if (backendPct > 5) {
      const etaSecs = Math.round(((100 - backendPct) / backendPct) * elapsedSeconds);
      etaLabel = `~${fmtSecs(etaSecs)} remaining`;
    }
  }

  // Compact "activity" line for the currently-running phase.
  let activityLine = "";
  if (currentPhase === "uploading") {
    activityLine = `Sending file to server — ${Math.min(uploadProgress * 2, 100)}%`;
  } else if (currentPhase === "parsing") {
    activityLine = processingRecordCount > 0
      ? `Parsed ${processingRecordCount.toLocaleString()} rows`
      : "Reading CSV rows…";
  } else if (currentPhase === "inserting") {
    if (batchesTotal > 0) {
      activityLine = `Batch ${batchesCompleted}/${batchesTotal} · ${recordsInserted.toLocaleString()} of ${processingRecordCount.toLocaleString()} rows written`;
    } else {
      activityLine = "Preparing parallel batches…";
    }
  } else if (currentPhase === "anomalies") {
    activityLine = "Scanning for suspicious donations…";
  } else if (currentPhase === "complete") {
    activityLine = "Almost done…";
  }

  return (
    <div className="space-y-5" data-testid="upload-progress-panel">
      {/* Phase strip */}
      <div className="flex items-center justify-center gap-1.5 flex-wrap" data-testid="phase-strip">
        {PHASE_ORDER.map((p) => {
          const meta = PHASE_META[p];
          const Icon = meta.icon;
          const idx = PHASE_ORDER.indexOf(p);
          const curIdx = PHASE_ORDER.indexOf(currentPhase);
          const state = idx < curIdx ? "done" : idx === curIdx ? "active" : "pending";
          const base = "px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-colors";
          const cls = state === "done"
            ? "bg-emerald-50 border-emerald-200 text-emerald-700"
            : state === "active"
              ? "bg-primary/10 border-primary/40 text-primary shadow-sm"
              : "bg-muted/40 border-transparent text-muted-foreground/60";
          return (
            <div key={p} className={`${base} ${cls}`} data-testid={`phase-pill-${p}`}>
              {state === "done"
                ? <Check className="w-3 h-3" />
                : state === "active"
                  ? <Loader2 className="w-3 h-3 animate-spin" />
                  : <Icon className="w-3 h-3" />}
              <span>{meta.label}</span>
            </div>
          );
        })}
      </div>

      {/* Big active phase card */}
      <div className="max-w-md mx-auto p-4 rounded-md border bg-card/60 backdrop-blur-sm">
        <div className="flex items-center gap-3">
          <div className="relative w-11 h-11 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
            <CurrentIcon className="w-5 h-5 text-primary" />
            {/* Heartbeat ping to reassure user something is actively happening */}
            <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-70" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
          </div>
          <div className="flex-1 min-w-0 text-left">
            <p className="font-medium text-sm" data-testid="current-phase-label">
              {PHASE_META[currentPhase].label}
            </p>
            <p className="text-xs text-muted-foreground truncate" data-testid="current-phase-activity">
              {activityLine || PHASE_META[currentPhase].tip}
            </p>
          </div>
        </div>

        <Progress value={uploadProgress} className="h-2 mt-3" data-testid="upload-progress-bar" />

        <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
          <span data-testid="elapsed-label">
            <Clock className="w-3 h-3 inline mr-1" />{elapsedLabel} elapsed
          </span>
          {etaLabel && <span data-testid="eta-label">{etaLabel}</span>}
        </div>
      </div>

      {/* Completed-phase timing rail — appears only once real timings arrive */}
      {(phaseTimings.parse_secs > 0 || phaseTimings.insert_secs > 0 || phaseTimings.anom_secs > 0) && (
        <div className="max-w-md mx-auto flex justify-center gap-4 text-xs text-muted-foreground" data-testid="phase-timings">
          {phaseTimings.parse_secs > 0 && (
            <span className="flex items-center gap-1">
              <ScanLine className="w-3 h-3" /> Parse {fmtSecs(phaseTimings.parse_secs)}
            </span>
          )}
          {phaseTimings.insert_secs > 0 && (
            <span className="flex items-center gap-1">
              <Database className="w-3 h-3" /> Insert {fmtSecs(phaseTimings.insert_secs)}
            </span>
          )}
          {phaseTimings.anom_secs > 0 && (
            <span className="flex items-center gap-1">
              <Siren className="w-3 h-3" /> Anomalies {fmtSecs(phaseTimings.anom_secs)}
            </span>
          )}
        </div>
      )}

      {/* Gentle reassurance for very large uploads */}
      {elapsedSeconds > 30 && uploadProgress < 90 && (
        <p className="text-xs text-muted-foreground/70 text-center max-w-sm mx-auto" data-testid="reassurance-note">
          Large FEC files can take 2–5 minutes. This page is safe to leave open — progress updates every second.
        </p>
      )}

      <div className="text-center">
        <Button
          variant="outline"
          size="sm"
          onClick={onCancel}
          className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          data-testid="cancel-upload-btn"
        >
          <Ban className="w-3.5 h-3.5 mr-1.5" />
          Cancel Import
        </Button>
      </div>
    </div>
  );
}

export default function UploadPage() {
  const { user } = useAuth();
  const { fetchDatasets: refreshDatasets, datasets, deleteDataset, updateDatasetName } = useDatasets();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadResult, setUploadResult] = useState(null);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");
  const [processingDatasetId, setProcessingDatasetId] = useState(null);
  const [processingRecordCount, setProcessingRecordCount] = useState(0);
  const [processingPhase, setProcessingPhase] = useState(""); // parsing|inserting|anomalies|complete
  const [recordsInserted, setRecordsInserted] = useState(0);
  const [batchesTotal, setBatchesTotal] = useState(0);
  const [batchesCompleted, setBatchesCompleted] = useState(0);
  const [phaseTimings, setPhaseTimings] = useState({ parse_secs: 0, insert_secs: 0, anom_secs: 0 });
  const [importStartTime, setImportStartTime] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [sortBy, setSortBy] = useState("date"); // "name" or "date"
  const [sortOrder, setSortOrder] = useState("desc"); // "asc" or "desc"
  const pollingRef = useRef(false);

  // Bulk URL import state
  const [bulkUrl, setBulkUrl] = useState("");
  const [bulkState, setBulkState] = useState("");
  const [bulkZips, setBulkZips] = useState("");
  const [bulkName, setBulkName] = useState("");
  const [bulkImporting, setBulkImporting] = useState(false);
  const [bulkProgress, setBulkProgress] = useState(0);
  const [bulkPhase, setBulkPhase] = useState("");
  const [bulkRecordCount, setBulkRecordCount] = useState(0);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkStartTime, setBulkStartTime] = useState(null);
  const [bulkElapsed, setBulkElapsed] = useState(0);
  const bulkPollingRef = useRef(false);
  const bulkDatasetIdRef = useRef(null);

  // Elapsed time ticker while uploading
  useEffect(() => {
    if (!uploading || !importStartTime) return;
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - importStartTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [uploading, importStartTime]);

  // Elapsed time ticker for bulk import
  useEffect(() => {
    if (!bulkImporting || !bulkStartTime) return;
    const timer = setInterval(() => {
      setBulkElapsed(Math.floor((Date.now() - bulkStartTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [bulkImporting, bulkStartTime]);

  const handleStartRename = (ds) => {
    setEditingId(ds.id);
    setEditName(ds.name || ds.filename.replace('.csv', ''));
  };

  const handleSaveRename = async () => {
    if (editingId && editName.trim()) {
      await updateDatasetName(editingId, editName.trim());
      setEditingId(null);
    }
  };

  const handleDownload = async (ds) => {
    try {
      const res = await fetch(`${API}/datasets/${ds.id}/download`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = (ds.name || ds.filename).replace(/\.csv$/, '') + '.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Failed to download dataset');
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = e.target.files;
    if (files.length > 0) {
      handleFileUpload(files[0]);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file.name.endsWith(".csv")) {
      toast.error("Invalid file type", {
        description: "Please upload a CSV file",
      });
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadResult(null);
    setImportStartTime(Date.now());
    setElapsedSeconds(0);
    setProcessingPhase("uploading");
    setRecordsInserted(0);
    setBatchesTotal(0);
    setBatchesCompleted(0);
    setPhaseTimings({ parse_secs: 0, insert_secs: 0, anom_secs: 0 });

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          ...authHeaders(),
        },
        timeout: 300000,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          // File upload is ~0-50% of the visual progress
          setUploadProgress(Math.round(progress * 0.5));
        },
      });

      const datasetId = response.data.dataset_id;
      const processingStatus = response.data.processing_status;

      if (processingStatus === "processing") {
        // Backend accepted the file — now poll for background processing status
        setUploadProgress(50);
        setProcessingDatasetId(datasetId);
        pollingRef.current = true;
        pollProcessingStatus(datasetId, file.name);
      } else {
        // Completed synchronously (small file)
        setUploading(false);
        setUploadResult({
          success: true,
          data: response.data,
          filename: file.name,
        });
        toast.success("Upload successful", {
          description: `${response.data.records_imported.toLocaleString()} records imported`,
        });
        setTimeout(() => refreshDatasets(), 500);
      }
    } catch (error) {
      console.error("Upload failed:", error);
      // Check if this is a timeout — the file may still be processing
      if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
        setUploadProgress(50);
        toast.info("Upload received", {
          description: "Processing large file in background...",
        });
        setTimeout(() => {
          refreshDatasets();
          setUploading(false);
          setUploadResult(null);
        }, 3000);
      } else if (error.response?.status === 409) {
        // Duplicate dataset
        setUploading(false);
        setUploadResult({
          success: false,
          error: error.response.data.detail,
          isDuplicate: true,
        });
        toast.error("Duplicate dataset", {
          description: error.response.data.detail,
        });
      } else {
        setUploading(false);
        setUploadResult({
          success: false,
          error: error.response?.data?.detail || "Upload failed",
        });
        toast.error("Upload failed", {
          description: error.response?.data?.detail || "An error occurred",
        });
      }
    }
  };

  const pollProcessingStatus = async (datasetId, filename) => {
    let attempts = 0;
    const maxAttempts = 300; // ~5 minutes at 1s intervals
    
    const poll = async () => {
      if (!pollingRef.current) return; // Cancelled
      attempts++;
      try {
        const res = await axios.get(`${API}/upload/status/${datasetId}`, {
          headers: authHeaders(),
          timeout: 10000,
        });
        const status = res.data;
        
        // Map backend progress (0-100) to visual progress (50-100)
        const visualProgress = 50 + Math.round((status.processing_progress || 0) * 0.5);
        setUploadProgress(Math.min(visualProgress, 99));
        setProcessingRecordCount(status.record_count || 0);
        setProcessingPhase(status.processing_phase || "");
        setRecordsInserted(status.records_inserted || 0);
        setBatchesTotal(status.batches_total || 0);
        setBatchesCompleted(status.batches_completed || 0);
        setPhaseTimings({
          parse_secs: status.parse_secs || 0,
          insert_secs: status.insert_secs || 0,
          anom_secs: status.anom_secs || 0,
        });

        if (status.processing_status === "complete") {
          pollingRef.current = false;
          setProcessingDatasetId(null);
          setUploading(false);
          setUploadProgress(100);
          setUploadResult({
            success: true,
            data: {
              records_imported: status.record_count,
              anomalies_detected: status.anomalies_detected || 0,
              date_range: status.date_range,
            },
            filename: filename,
          });
          toast.success("Upload complete", {
            description: `${status.record_count.toLocaleString()} records imported`,
          });
          refreshDatasets();
          return;
        }
        
        if (status.processing_status === "failed") {
          pollingRef.current = false;
          setProcessingDatasetId(null);
          setUploading(false);
          setUploadResult({
            success: false,
            error: status.processing_error || "Processing failed",
          });
          toast.error("Processing failed", {
            description: status.processing_error || "An error occurred during processing",
          });
          return;
        }

        // Still processing — continue polling
        if (attempts < maxAttempts && pollingRef.current) {
          setTimeout(poll, 1000);
        } else {
          pollingRef.current = false;
          setProcessingDatasetId(null);
          setUploading(false);
          setUploadResult({
            success: false,
            error: "Processing is taking longer than expected. The dataset may still appear shortly.",
          });
        }
      } catch (err) {
        if (!pollingRef.current) return; // Cancelled during poll
        console.error("Status poll error:", err);
        // 404 means dataset was deleted (cancelled)
        if (err.response?.status === 404) {
          pollingRef.current = false;
          setProcessingDatasetId(null);
          setUploading(false);
          setUploadResult(null);
          return;
        }
        if (attempts < maxAttempts && pollingRef.current) {
          setTimeout(poll, 2000);
        } else {
          pollingRef.current = false;
          setProcessingDatasetId(null);
          setUploading(false);
          setUploadResult(null);
          refreshDatasets();
        }
      }
    };
    
    setTimeout(poll, 1000);
  };

  const handleCancelUpload = async () => {
    pollingRef.current = false;
    if (processingDatasetId) {
      try {
        await deleteDataset(processingDatasetId);
        toast.info("Upload cancelled", { description: "Dataset and partial records have been removed." });
      } catch (e) {
        console.error("Failed to cancel:", e);
      }
    }
    setProcessingDatasetId(null);
    setUploading(false);
    setUploadProgress(0);
    setUploadResult(null);
    refreshDatasets();
  };

  const handleDeleteDataset = async (datasetId) => {
    await deleteDataset(datasetId);
    toast.success("Dataset deleted");
  };

  const handleBulkImport = async () => {
    if (!bulkUrl.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    const zipList = bulkZips
      .split(",")
      .map((z) => z.trim())
      .filter(Boolean);

    setBulkImporting(true);
    setBulkProgress(0);
    setBulkPhase("starting");
    setBulkRecordCount(0);
    setBulkResult(null);
    setBulkStartTime(Date.now());
    setBulkElapsed(0);
    bulkPollingRef.current = true;

    try {
      const res = await axios.post(
        `${API}/import/bulk-url`,
        {
          url: bulkUrl.trim(),
          state_filter: bulkState.trim() || null,
          zip_codes: zipList.length > 0 ? zipList : null,
          dataset_name: bulkName.trim() || null,
        },
        { headers: authHeaders(), timeout: 30000 }
      );
      const datasetId = res.data.dataset_id;
      bulkDatasetIdRef.current = datasetId;
      pollBulkStatus(datasetId);
    } catch (err) {
      setBulkImporting(false);
      bulkPollingRef.current = false;
      toast.error("Import failed", {
        description: err.response?.data?.detail || "Could not start bulk import",
      });
    }
  };

  const pollBulkStatus = async (datasetId) => {
    let attempts = 0;
    const maxAttempts = 3600; // Up to 1 hour at 1s intervals

    const poll = async () => {
      if (!bulkPollingRef.current) return;
      attempts++;
      try {
        const res = await axios.get(`${API}/upload/status/${datasetId}`, {
          headers: authHeaders(),
          timeout: 10000,
        });
        const s = res.data;
        setBulkProgress(s.processing_progress || 0);
        setBulkPhase(s.processing_phase || "processing");
        setBulkRecordCount(s.record_count || 0);

        if (s.processing_status === "complete") {
          bulkPollingRef.current = false;
          setBulkImporting(false);
          setBulkResult({
            success: true,
            records: s.record_count,
            anomalies: s.anomalies_detected || 0,
            dateRange: s.date_range,
          });
          toast.success("Bulk import complete", {
            description: `${(s.record_count || 0).toLocaleString()} records imported`,
          });
          refreshDatasets();
          return;
        }
        if (s.processing_status === "failed") {
          bulkPollingRef.current = false;
          setBulkImporting(false);
          setBulkResult({ success: false, error: s.processing_error });
          toast.error("Import failed", { description: s.processing_error });
          return;
        }
        if (attempts < maxAttempts && bulkPollingRef.current) {
          setTimeout(poll, 1000);
        }
      } catch (err) {
        if (!bulkPollingRef.current) return;
        if (err.response?.status === 404) {
          bulkPollingRef.current = false;
          setBulkImporting(false);
          setBulkResult(null);
          return;
        }
        if (attempts < maxAttempts && bulkPollingRef.current) {
          setTimeout(poll, 2000);
        }
      }
    };
    setTimeout(poll, 1000);
  };

  const handleCancelBulkImport = async () => {
    bulkPollingRef.current = false;
    if (bulkDatasetIdRef.current) {
      try {
        await deleteDataset(bulkDatasetIdRef.current);
        toast.info("Bulk import cancelled");
      } catch (e) {
        console.error("Cancel failed:", e);
      }
    }
    bulkDatasetIdRef.current = null;
    setBulkImporting(false);
    setBulkProgress(0);
    setBulkResult(null);
    refreshDatasets();
  };

  const formatElapsed = (secs) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  if (user?.role === 'viewer') {
    return (
      <div className="min-h-screen p-8 flex items-center justify-center" data-testid="upload-page">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Ban className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
            <h2 className="font-heading text-xl font-semibold mb-2">View-Only Access</h2>
            <p className="text-sm text-muted-foreground">Your account has Viewer permissions. Uploading data requires a User or Superadmin role.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen p-8 relative"
      style={{
        backgroundImage: `url('https://images.unsplash.com/photo-1765046255517-412341954c4c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhdGElMjB2aXN1YWxpemF0aW9uJTIwbWluaW1hbGlzdCUyMHdoaXRlJTIwYmFja2dyb3VuZHxlbnwwfHx8fDE3NzM3MjQ0MjN8MA&ixlib=rb-4.1.0&q=85')`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      data-testid="upload-page"
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white/80" />

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto space-y-6 md:space-y-8 px-4 md:px-0">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading text-2xl md:text-4xl font-semibold tracking-tight">
            Upload FEC Data
          </h1>
          <p className="text-muted-foreground mt-2">
            Import CSV files from the Federal Election Commission
          </p>
          <a
            href="https://www.fec.gov/data/receipts/individual-contributions/?data_type=processed"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg transition-colors"
            data-testid="fec-data-link"
          >
            <ExternalLink className="w-4 h-4" />
            Get FEC Data
          </a>
        </div>

        {/* Upload Area */}
        <Card className="border rounded-sm shadow-sm" data-testid="upload-card">
          <CardContent className="p-8">
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-sm p-12 text-center transition-all duration-200 ${
                isDragging
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
              }`}
              data-testid="dropzone"
            >
              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                className="hidden"
                id="file-input"
                data-testid="file-input"
              />

              {uploading ? (
                <UploadProgressPanel
                  uploadProgress={uploadProgress}
                  processingPhase={processingPhase}
                  processingRecordCount={processingRecordCount}
                  recordsInserted={recordsInserted}
                  batchesTotal={batchesTotal}
                  batchesCompleted={batchesCompleted}
                  phaseTimings={phaseTimings}
                  elapsedSeconds={elapsedSeconds}
                  onCancel={handleCancelUpload}
                />
              ) : uploadResult ? (
                <div className="space-y-4">
                  {uploadResult.success ? (
                    <>
                      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                        <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                      </div>
                      <div>
                        <h3 className="font-heading text-lg font-medium text-emerald-700">
                          Upload Successful!
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {uploadResult.filename}
                        </p>
                      </div>
                      <div className="flex justify-center gap-6 text-sm">
                        <div className="text-center">
                          <p className="font-mono text-xl font-semibold">
                            {uploadResult.data.records_imported.toLocaleString()}
                          </p>
                          <p className="text-muted-foreground">Records</p>
                        </div>
                        <div className="text-center">
                          <p className="font-mono text-xl font-semibold text-red-600">
                            {uploadResult.data.anomalies_detected}
                          </p>
                          <p className="text-muted-foreground">Anomalies</p>
                        </div>
                        <div className="text-center">
                          <p className="font-mono text-xl font-semibold">
                            {uploadResult.data.date_range}
                          </p>
                          <p className="text-muted-foreground">Date Range</p>
                        </div>
                      </div>
                      <Button
                        onClick={() => setUploadResult(null)}
                        variant="outline"
                        className="rounded-sm mt-4"
                        data-testid="upload-another-btn"
                      >
                        Upload Another File
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${uploadResult.isDuplicate ? 'bg-amber-100' : 'bg-red-100'}`}>
                        {uploadResult.isDuplicate
                          ? <AlertCircle className="w-8 h-8 text-amber-600" />
                          : <AlertCircle className="w-8 h-8 text-red-600" />
                        }
                      </div>
                      <div>
                        <h3 className={`font-heading text-lg font-medium ${uploadResult.isDuplicate ? 'text-amber-700' : 'text-red-700'}`}>
                          {uploadResult.isDuplicate ? 'Duplicate Dataset' : 'Upload Failed'}
                        </h3>
                        <p className="text-sm text-muted-foreground mt-1">
                          {uploadResult.error}
                        </p>
                      </div>
                      <Button
                        onClick={() => setUploadResult(null)}
                        variant="outline"
                        className="rounded-sm mt-4"
                        data-testid="try-again-btn"
                      >
                        {uploadResult.isDuplicate ? 'Upload Different File' : 'Try Again'}
                      </Button>
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="w-16 h-16 mx-auto rounded-full bg-muted flex items-center justify-center mb-4">
                    <UploadCloud className="w-8 h-8 text-muted-foreground" />
                  </div>
                  <h3 className="font-heading text-lg font-medium">
                    Drop your CSV file here
                  </h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    or click to browse
                  </p>
                  <label htmlFor="file-input">
                    <Button
                      asChild
                      variant="default"
                      className="rounded-sm mt-4 cursor-pointer"
                    >
                      <span data-testid="select-file-btn">Select File</span>
                    </Button>
                  </label>
                  <p className="text-xs text-muted-foreground mt-4">
                    Supports FEC contribution CSV files
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Import from URL */}
        <Card className="border rounded-sm shadow-sm" data-testid="bulk-import-card">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg font-medium flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Import from FEC Bulk Download
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {bulkImporting ? (
              <div className="text-center space-y-3 py-4">
                <div className="w-14 h-14 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <Globe className="w-7 h-7 text-primary animate-pulse" />
                </div>
                <Progress value={bulkProgress} className="h-2 max-w-xs mx-auto" />
                <p className="text-sm text-muted-foreground">
                  {bulkPhase === "downloading" && `Downloading file... ${bulkProgress}%`}
                  {bulkPhase === "parsing" && `Scanning & filtering records...`}
                  {bulkPhase === "importing" && `Importing ${bulkRecordCount > 0 ? bulkRecordCount.toLocaleString() + ' ' : ''}records... ${bulkProgress}%`}
                  {bulkPhase === "anomalies" && "Detecting anomalies..."}
                  {(!bulkPhase || bulkPhase === "starting") && "Starting import..."}
                </p>
                {bulkElapsed > 0 && (
                  <p className="text-xs text-muted-foreground/70">
                    {formatElapsed(bulkElapsed)} elapsed
                    {bulkProgress > 5 && bulkProgress < 99 && (() => {
                      const eta = Math.round(((100 - bulkProgress) / bulkProgress) * bulkElapsed);
                      return ` · ~${formatElapsed(eta)} remaining`;
                    })()}
                  </p>
                )}
                <Button variant="outline" size="sm" onClick={handleCancelBulkImport}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  data-testid="cancel-bulk-import-btn">
                  <Ban className="w-3.5 h-3.5 mr-1.5" /> Cancel Import
                </Button>
              </div>
            ) : bulkResult ? (
              <div className="text-center space-y-3 py-4">
                {bulkResult.success ? (
                  <>
                    <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
                      <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-medium text-emerald-700">Import Complete!</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {bulkResult.records.toLocaleString()} records · {bulkResult.anomalies} anomalies · {bulkResult.dateRange}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setBulkResult(null)} data-testid="bulk-import-again-btn">
                      Import Another
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 mx-auto rounded-full bg-red-100 flex items-center justify-center">
                      <AlertCircle className="w-7 h-7 text-red-600" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-medium text-red-700">Import Failed</h3>
                      <p className="text-sm text-muted-foreground mt-1">{bulkResult.error}</p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => setBulkResult(null)}>Try Again</Button>
                  </>
                )}
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground">
                  Import directly from FEC bulk download files (pipe-delimited .zip). Use filters to import only the records you need.
                </p>
                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Download URL</label>
                    <Input
                      value={bulkUrl}
                      onChange={(e) => setBulkUrl(e.target.value)}
                      placeholder="https://www.fec.gov/files/bulk-downloads/2020/indiv20.zip"
                      data-testid="bulk-url-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">
                        <Filter className="w-3 h-3 inline mr-1" />State Filter (optional)
                      </label>
                      <Input
                        value={bulkState}
                        onChange={(e) => setBulkState(e.target.value.toUpperCase().slice(0, 2))}
                        placeholder="e.g. CA"
                        maxLength={2}
                        data-testid="bulk-state-input"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-muted-foreground mb-1 block">
                        <Filter className="w-3 h-3 inline mr-1" />ZIP Codes (comma-separated)
                      </label>
                      <Input
                        value={bulkZips}
                        onChange={(e) => setBulkZips(e.target.value)}
                        placeholder="e.g. 90210, 90069, 90048"
                        data-testid="bulk-zips-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground mb-1 block">Dataset Name (optional)</label>
                    <Input
                      value={bulkName}
                      onChange={(e) => setBulkName(e.target.value)}
                      placeholder="Auto-generated from filters if empty"
                      data-testid="bulk-name-input"
                    />
                  </div>
                  <Button onClick={handleBulkImport} className="w-full" data-testid="start-bulk-import-btn">
                    <Globe className="w-4 h-4 mr-2" /> Start Import
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Uploaded Datasets */}
        <Card className="border rounded-sm shadow-sm" data-testid="datasets-list">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-lg font-medium">
                Uploaded Datasets
              </CardTitle>
              {datasets.length > 1 && (
                <div className="flex items-center gap-1" data-testid="sort-controls">
                  <Button
                    variant={sortBy === "name" ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs px-2.5"
                    onClick={() => {
                      if (sortBy === "name") setSortOrder(o => o === "asc" ? "desc" : "asc");
                      else { setSortBy("name"); setSortOrder("asc"); }
                    }}
                    data-testid="sort-by-name"
                  >
                    <ArrowUpDown className="w-3 h-3 mr-1" />
                    Name {sortBy === "name" ? (sortOrder === "asc" ? "A-Z" : "Z-A") : ""}
                  </Button>
                  <Button
                    variant={sortBy === "date" ? "default" : "outline"}
                    size="sm"
                    className="h-7 text-xs px-2.5"
                    onClick={() => {
                      if (sortBy === "date") setSortOrder(o => o === "asc" ? "desc" : "asc");
                      else { setSortBy("date"); setSortOrder("desc"); }
                    }}
                    data-testid="sort-by-date"
                  >
                    <ArrowUpDown className="w-3 h-3 mr-1" />
                    Date {sortBy === "date" ? (sortOrder === "desc" ? "Newest" : "Oldest") : ""}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingDatasets ? (
              <div className="space-y-3">
                {[1, 2].map((i) => (
                  <div
                    key={i}
                    className="h-16 bg-muted animate-pulse rounded-sm"
                  />
                ))}
              </div>
            ) : datasets.length === 0 ? (
              <div className="py-8 text-center">
                <FileSpreadsheet className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">No datasets uploaded yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {[...datasets].sort((a, b) => {
                  if (sortBy === "name") {
                    const nameA = (a.name || a.filename || "").toLowerCase();
                    const nameB = (b.name || b.filename || "").toLowerCase();
                    return sortOrder === "asc" ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
                  }
                  // sort by date
                  const dateA = a.upload_date || "";
                  const dateB = b.upload_date || "";
                  return sortOrder === "desc" ? dateB.localeCompare(dateA) : dateA.localeCompare(dateB);
                }).map((dataset, index) => (
                  <div
                    key={dataset.id}
                    className="flex items-center justify-between p-4 border rounded-sm hover:bg-muted/30 transition-colors animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                    data-testid={`dataset-item-${index}`}
                  >
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                      <div className="p-2 bg-muted rounded-sm shrink-0">
                        <FileSpreadsheet className="w-5 h-5 text-primary" />
                      </div>
                      {editingId === dataset.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <Input
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="h-8 text-sm"
                            autoFocus
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') handleSaveRename();
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            data-testid={`rename-input-${index}`}
                          />
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={handleSaveRename}>
                            <Check className="w-4 h-4 text-emerald-600" />
                          </Button>
                          <Button size="icon" variant="ghost" className="h-7 w-7 shrink-0" onClick={() => setEditingId(null)}>
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ) : (
                        <div className="min-w-0">
                          <p className="font-medium text-sm truncate">{dataset.name || dataset.filename}</p>
                          <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground flex-wrap">
                            <span className="flex items-center gap-1">
                              <Hash className="w-3 h-3" />
                              {dataset.record_count?.toLocaleString()} records
                            </span>
                            {dataset.date_range && (
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {dataset.date_range}
                              </span>
                            )}
                            {dataset.upload_date && (
                              <span className="flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {new Date(dataset.upload_date).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}{' '}
                                {new Date(dataset.upload_date).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    {editingId !== dataset.id && (
                      <div className="flex items-center gap-1 shrink-0 ml-2">
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleStartRename(dataset)}
                          className="text-muted-foreground hover:text-foreground"
                          title="Rename"
                          data-testid={`rename-dataset-${index}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleDownload(dataset)}
                          className="text-muted-foreground hover:text-blue-600"
                          title="Download CSV"
                          data-testid={`download-dataset-${index}`}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost" size="sm"
                          onClick={() => handleDeleteDataset(dataset.id)}
                          className="text-muted-foreground hover:text-red-600"
                          data-testid={`delete-dataset-${index}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
